import { type FC, type ReactNode } from 'react';
import { type BaseProps } from 'src/modules/shared/types/component.type';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import {
  emptyStateBaseClasses,
  emptyStateSizeClasses,
  emptyStateFilledClasses,
  emptyStateOutlineClasses,
  emptyStateSoftClasses,
  emptyStateGhostClasses,
  iconContainerSizeClasses,
  iconSizeClasses,
  iconContainerClasses,
  horizontalGapClasses,
  verticalSpaceClasses,
  textSpaceClasses,
  getTitleTextSize,
  getDescriptionTextSize,
} from './empty-state.styles';

/**
 * Props interface for the EmptyState component
 */
interface EmptyStateProps
  extends Omit<BaseProps, 'children' | 'state' | 'loading' | 'error'> {
  /** Title text to display */
  title?: string;
  /** Description text to display below the title */
  description?: string;
  /** Icon to display above the text content */
  icon?: ReactNode;
  /** Layout direction for content arrangement */
  placement?: 'horizontal' | 'vertical';
  /** Callback function for the action button */
  onAction?: () => void;
  /** Content to display inside the action button */
  actionContent?: ReactNode;
}

/**
 * EmptyState component that displays placeholder content when no data is available.
 * Supports various visual styles through variant, appearance, and size props.
 *
 * @param title - Primary text to display
 * @param description - Secondary descriptive text
 * @param icon - Icon to display above the text
 * @param placement - Layout orientation (horizontal or vertical)
 * @param onAction - Callback for action button click
 * @param actionContent - Content for the action button
 * @param variant - Color variant (primary, secondary, neutral, danger, warning, success, info)
 * @param appearance - Visual style (filled, outline, soft, ghost)
 * @param size - Component size (2xs, xs, sm, md, lg)
 * @param disabled - Whether the component is in disabled state
 * @param className - Additional CSS classes
 */
export const EmptyState: FC<EmptyStateProps> = ({
  title = '',
  description = '',
  icon,
  className = '',
  disabled = false,
  placement = 'vertical',
  onAction,
  actionContent,
  variant = 'neutral',
  appearance = 'soft',
  size = 'md',
}) => {
  const appearanceClassMap = {
    filled: emptyStateFilledClasses,
    outline: emptyStateOutlineClasses,
    soft: emptyStateSoftClasses,
    ghost: emptyStateGhostClasses,
  };

  const containerLayoutClasses =
    placement === 'horizontal'
      ? `flex items-center justify-center ${horizontalGapClasses[size]}`
      : `flex flex-col items-center justify-center ${verticalSpaceClasses[size]}`;

  const textAlignmentClasses =
    placement === 'horizontal' ? 'text-left !mt-0' : 'text-center';

  const allClasses = [
    ...emptyStateBaseClasses,
    emptyStateSizeClasses[size],
    appearanceClassMap[appearance][variant],
    containerLayoutClasses,
    disabled ? 'opacity-50 cursor-not-allowed' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const titleTextSize = getTitleTextSize(size);
  const descriptionTextSize = getDescriptionTextSize(size);

  return (
    <div className={allClasses}>
      {icon && (
        <div
          className={`
            flex-shrink-0 rounded-full flex items-center justify-center
            ${iconContainerSizeClasses[size]}
            ${iconContainerClasses[appearance][variant]}
            ${placement === 'horizontal' ? '' : 'mx-auto'}
          `}
        >
          <div
            className={`${iconSizeClasses[size]} flex items-center justify-center`}
          >
            {icon}
          </div>
        </div>
      )}

      <div className={`${textSpaceClasses[size]} ${textAlignmentClasses}`}>
        {title && (
          <Text size={titleTextSize} weight="medium" color={variant}>
            {title}
          </Text>
        )}
        {description && (
          <Text size={descriptionTextSize} color={variant}>
            {description}
          </Text>
        )}
      </div>

      {onAction && actionContent && (
        <Button
          variant={variant}
          appearance={appearance}
          onClick={onAction}
          size={size}
          disabled={disabled}
        >
          {actionContent}
        </Button>
      )}
    </div>
  );
};
